import React from 'react';
import { observer } from 'mobx-react';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { Modal } from 'ui/base/modal';
import { InputKeyEvent } from 'ui/base/types';
import { BackButton } from 'ui/base/button/back_button';
import { ScrollableArea } from 'ui/base/page/scrollable_area';
import { TextInput } from 'ui/public/input';
import { NetworkInputStore } from './network_input_store';
import baseStyles from './network_input_modal.module.css';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import Messages from './network_input_messages';

type NetworkInputStyles = {
  modalContainer: string;
  column: string;
  headerContainer: string;
  contentContainer: string;
  instructions: string;
  inputContainer: string;
  errorContainer: string;
  centered: string;
  accept: string;
};

const themedStyles: ThemedStyles<NetworkInputStyles> = {
  [Theme.DAYLIGHT]: {
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    contentContainer: baseStyles.contentContainer,
    instructions: baseStyles.instructions,
    inputContainer: baseStyles.inputContainer,
    errorContainer: baseStyles.errorContainer,
    centered: baseStyles.centered,
    accept: baseStyles.accept,
  },
  [Theme.MIDNIGHT]: {
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    contentContainer: baseStyles.contentContainer,
    instructions: baseStyles.instructions,
    inputContainer: baseStyles.inputContainer,
    errorContainer: baseStyles.errorContainer,
    centered: baseStyles.centered,
    accept: baseStyles.accept,
  },
};

type NetworkInputModalProps = {
  store: NetworkInputStore;
  instructions: string;
  saveMessage: string;
  onCommit(): void;
  invalidMessage: string;
};

export const NetworkInputModal = observer(({
  store,
  instructions,
  saveMessage,
  onCommit,
  invalidMessage,
}: NetworkInputModalProps) => {
  const styles = useStyles(themedStyles);

  /*
  The implementation here is a hack to get around the HTML restriction
  that forms cannot be nested. This 'modal' may or may not be contained
  by some enclosing form (though I think, in all usages, there's an
  enclosing form).

  Pressing 'Enter' on an input contained in a form
  triggers the 'submit' of that form, which we do not want here; we
  only want to call onCommit (the action of this 'modal'). So we
  capture the Enter event.

  The submit button is also a div so that it doesn't trigger a submit.

  I think a better implementation would be to not place modal components
  inside the components that trigger opening them. A special separate
  handling of modals might be better.
  */
  const handleKeyPress = React.useCallback((event: InputKeyEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onCommit();
    }
  }, [onCommit]);
  return (
    <Modal store={store.modal}>
      <div className={styles.modalContainer}>
        <ScrollableArea>
          <div className={styles.column}>
            <div className={styles.headerContainer}>
              <BackButton onClick={store.modal.hideModal}/>
            </div>
            <div className={styles.contentContainer}>
              <div>
                <div className={styles.instructions}>
                  {instructions}
                </div>
                <div className={styles.inputContainer}>
                  <TextInput
                    placeholder={Messages.address()}
                    store={store.address}
                    onKeyPress={handleKeyPress}/>/
                  <TextInput
                    placeholder={Messages.mask()}
                    store={store.mask}
                    onKeyPress={handleKeyPress}/>
                </div>
                {!store.isValid.value &&
                  <div className={styles.errorContainer}>
                    <ErrorMessage message={invalidMessage}/>
                  </div>
                }
                <div className={styles.centered} onClick={onCommit}>
                  <div className={styles.accept}>
                    {saveMessage}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollableArea>
      </div>
    </Modal>
  );
});
